import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { t } from "i18next";
import { getCookie, setCookie } from "src/utils/global-functions";

import { LinearProgress, Box, Typography } from "@mui/material";

const VERSION_KEY = "fe_version_number";

export default function VersionCheckSnackbar() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [versionToUpdate, setVersionToUpdate] = useState<string | null>(null);
  const [snackbarKey, setSnackbarKey] = useState<any>(null);

  useEffect(() => {
    let disposed = false;
    let intervalId: ReturnType<typeof setInterval>;

    const checkVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
        });

        if (!res.ok) return;

        const data = await res.json();
        const nextVersion = String(data?.version ?? "").trim();
        if (!nextVersion) return;

        const storedVersion = getCookie(VERSION_KEY) ?? "";

        if (!storedVersion) {
          setCookie(VERSION_KEY, nextVersion);
          return;
        }

        if (storedVersion !== nextVersion) {
          clearInterval(intervalId);

          if (!disposed) {
            setVersionToUpdate(nextVersion);
          }
        }
      } catch (error) {
        // Intentionally ignored:
        // happens during deploy/nginx reload/offline/tab resume.
        console.debug("version_check_failed", error);
      }
    };

    checkVersion();

    intervalId = setInterval(checkVersion, 30 * 1000);

    return () => {
      disposed = true;
      clearInterval(intervalId);
    };
  }, []);

  // Show message and reload after 30 seconds
  useEffect(() => {
    if (versionToUpdate && !snackbarKey) {
      const key = enqueueSnackbar("", {
        persist: true,
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        hideIconVariant: true,
        content: (key) => (
          <div
            style={{
              margin: 0,
              padding: 0,
              background: "transparent",
              boxShadow: "none",
              width: "100%",
            }}
          >
            <div
              style={{
                maxWidth: 720,
                width: "calc(100% - 16px)",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 6px",
                borderRadius: 10,
                fontSize: 10,
                lineHeight: 1,
                background: "rgba(25,118,210,0.08)", // subtle info-ish bg; tweak to taste
                // backdropFilter: "blur(6px)",
                boxShadow: "0 0 0 1px rgba(25,118,210,0.25)",
                color: "inherit",
              }}
            >
              <span
                style={{
                  // whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {t("new_version_available_auto_refresh_in_30_seconds")}
              </span>

              <button
                onClick={() => {
                  setCookie(VERSION_KEY, versionToUpdate);
                  closeSnackbar(key);
                  window.location.reload();
                }}
                style={{
                  background: "#2e7d32",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.8)",
                  borderRadius: 999,
                  padding: "2px 8px",
                  fontSize: 10,
                  lineHeight: 1.5,
                  cursor: "pointer",
                }}
              >
                {t("refresh_now")}
              </button>
            </div>

            {/* Tiny progress strip (optional): */}
            <div
              style={{
                maxWidth: 720,
                width: "calc(100% - 16px)",
                margin: "4px auto 0",
                height: 3,
                borderRadius: 999,
                overflow: "hidden",
                background: "rgba(25,118,210,0.18)",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: "100%",
                  transformOrigin: "left",
                  background: "rgba(25,118,210,0.9)",
                  animation: "fs-ver-bar 30s linear forwards",
                }}
              />
            </div>

            <style>
              {`@keyframes fs-ver-bar { from { transform: scaleX(0) } to { transform: scaleX(1) } }`}
            </style>
          </div>
        ),
      });

      // const key = enqueueSnackbar(
      //   <>{t("new_version_available_auto_refresh_in_30_seconds")}</>,
      //   {
      //     variant: "info",
      //     persist: true,
      //     action: (key) => (
      //       <button
      //         onClick={() => {
      //           setCookie(VERSION_KEY, versionToUpdate);
      //           closeSnackbar(key);
      //           window.location.reload();
      //         }}
      //         style={{
      //           background: "green",
      //           color: "#fff",
      //           border: "1px solid #fff",
      //           borderRadius: "4px",
      //           padding: "2px 8px",
      //           marginLeft: "8px",
      //           cursor: "pointer",
      //         }}
      //       >
      //         {t("refresh_now")}
      //       </button>
      //     ),
      //   }
      // );

      setSnackbarKey(key);

      setTimeout(() => {
        setCookie(VERSION_KEY, versionToUpdate);
        window.location.reload();
      }, 30 * 1000);
    }
  }, [versionToUpdate, enqueueSnackbar, closeSnackbar, snackbarKey]);

  return null;
}
